<template>
  <Modal
    :modal-types="[EMAIL_MODAL]"
    font-awesome-icon-class="fas fa-fw fa-at"
    modal-title="Email Chart"
    :should-close-modal-on-background-click="false"
  >
    <div v-show="isInitializingModal" class="loading-spinner-container">
      <i class="fad fa-fw fa-spinner-third fa-spin fa-2x"></i>
    </div>
    <div v-show="!isInitializingModal && failedToCreateLinkToChart" class="failed-to-load-container">
      <div class="error-message">
        Failed to create chart URL link. Please try again.
      </div>
      <button
        type="button"
        class="sc3-button sc3-button-green"
        @click="generateLinkToChart()"
      >
        <i class="fas fa-redo"></i>
        &nbsp;Retry
      </button>
      <button
        type="button"
        class="sc3-button-link"
        @click="closeModal()"
      >
        Close
      </button>
    </div>
    <div v-show="!isInitializingModal && !failedToCreateLinkToChart" class="email-ui-container">
      <div v-if="!isOnEmailPreviewScreen" class="modal-body">
        <p class="info">Share this chart with a friend via email</p>
        <div v-show="errorMessage" class="error-message">{{ errorMessage }}</div>
        <label
          for="email-input-box"
          class="sc3-form-label sc3-form-label-inline sc3-form-label-small"
        >SEND TO</label>
        <input
          id="email-input-box"
          type="email"
          v-model="email"
          class="sc3-text-input sc3-text-input-full-width"
          placeholder="Enter Email Address"
          autocomplete="off"
        />
        <label
          for="email-comments-input-box"
          class="sc3-form-label sc3-form-label-inline sc3-form-label-small"
        >YOUR COMMENTS</label>
        <textarea
          id="email-comments-input-box"
          type="text"
          v-model="emailComments"
          class="sc3-textarea"
          placeholder="Add a comment about this chart to your email"
          autocomplete="off"
        ></textarea>
      </div>
      <div v-else class="modal-body">
        <p class="title-message">PREVIEW AND SEND</p>
        <div class="email-preview-body">
          <a href="/" title="StockCharts.com">
            <img class="header-logo" :src="`//d.${VITE_APP_ROOT_DOMAIN}/img/email-scclogo2.png`" alt="StockCharts.com">
          </a>
          <div class="email-preview-inner-body">
            <p><b>Hey there!</b></p>
            <p>Your friend {{ memberFullName }} has sent you the following SharpChart snapshot from StockCharts.com:</p>
            <img
              alt="Email Preview Chart"
              :src="layoutStore.chartImageUrlString"
            />
            <p v-if="emailComments">Comments from {{ memberFullName }}:</p>
            <p v-if="emailComments"><b>{{ emailComments }}</b></p>
            <p class="gray-text-line">
              You can access the "live" online version of this chart at the link below:<br>
              <a :href="clickableLink" class="scc-link" title="'live' online version of this chart" rel="noopener noreferrer">{{ clickableLink }}</a>
            </p>
            <p class="gray-text-line">Thanks for using StockCharts!</p>
            <div class="gray-text-line">
              <p>Happy Charting,</p>
              <a href="/" class="scc-signature"><b>The StockCharts Team</b></a>
            </div>
            <i class="special-note">
              Please Note:
              <br>
              Some advanced features and settings may not be available to non-members or Basic members.
            </i>
          </div>
          <div class="footer">
            <div class="social-media-links">
              <a href="https://www.facebook.com/stockchartsdotcom" title="Facebook" rel="noopener noreferrer">
                <img :src="`//d.${VITE_APP_ROOT_DOMAIN}/img/mailfooter_fb.jpg`" alt="Facebook">
              </a>
              <a href="https://twitter.com/stockcharts" title="Twitter" rel="noopener noreferrer">
                <img :src="`//d.${VITE_APP_ROOT_DOMAIN}/img/mailfooter_twtr.jpg`" alt="Twitter">
              </a>
              <a href="https://www.youtube.com/user/stockchartscom" title="YouTube" rel="noopener noreferrer">
                <img :src="`//d.${VITE_APP_ROOT_DOMAIN}/img/mailfooter_youtube.jpg`" alt="YouTube">
              </a>
            </div>
            <div class="copyright-notice">
              <a href="/"><b>© StockCharts.com, Inc.</b></a>
              <br>
              All Rights Reserved.
            </div>
            <div class="potential-disclaimer-notice">
              This email was sent to {{ email }} by {{ memberEmail }}.
              If you think that you have received this chart in error, or to report abuse of our emailing feature, please contact
              <a href="mailto:support@stockcharts.com" class="mail-to-tag" rel="noopener noreferrer">Support@StockCharts.com</a>.
            </div>
            <div class="legal-statement-container">
              <a href="//help.stockcharts.com/learning-more/policies-and-limitations/terms-of-service" class="legal-statement" rel="noopener noreferrer">Terms of Service</a>
              &nbsp;|&nbsp;
              <a href="//help.stockcharts.com/learning-more/policies-and-limitations/privacy-statement" class="legal-statement" rel="noopener noreferrer">Privacy Statement</a>
            </div>
          </div>
        </div>
      </div>
      <hr class="sc3-form-divider" />
      <div v-if="!isOnEmailPreviewScreen" class="button-row">
        <button
          type="submit"
          class="sc3-button sc3-button-green"
          @click="previewEmail()"
        >
          <i class="fas fa-eye"></i>
          &nbsp;Preview Email
        </button>
        <button
          type="button"
          class="sc3-button-link"
          @click="closeModal()"
        >
          Close
        </button>
      </div>
      <div v-else class="button-row">
        <button
          type="submit"
          class="sc3-button sc3-button-green"
          :disabled="isSendingEmail"
          @click="onEmailSend()"
        >
          <i class="fas fa-paper-plane" v-show="!isSendingEmail"></i>
          <i class="fad fa-fw fa-spinner-third fa-spin" v-show="isSendingEmail"></i>
          &nbsp;Send Email
        </button>
        <button
          type="button"
          class="sc3-button-link"
          @click="goBackFromEmailPreview()"
          :disabled="isSendingEmail"
        >
          Back
        </button>
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
  import { ref, computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useModals } from '@/composables/useModals';
  import { usePermalink } from '@/composables/usePermalink';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import Modal from '@/components/modals/Modal.vue';

  const layoutStore = useLayoutStore();
  const { modalType } = storeToRefs(layoutStore);
  const { memberFullName, memberEmail } = useAuthentication();
  const {
    EMAIL_MODAL,
    closeModal
  } = useModals();
  const { getLinkableVersionLong, getShortUrl } = usePermalink();
  const { addGreenActionResultMessage, addRedActionResultMessage } = useActionResultMessages();
  const { VITE_APP_ROOT_DOMAIN } = import.meta.env;
  const longLinkToChart = ref('');
  const linkToChart = ref('');
  const failedToCreateLinkToChart = ref(false);
  const MAX_EMAIL_COMMENTS_LENGTH = 1000;
  const errorMessage = ref('');
  const email = ref('');
  const emailComments = ref('');
  const hasClickedToPreviewEmail = ref(false);
  const isInitializingModal = ref(false);
  const isSendingEmail = ref(false);
  const emailValidationRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

  const isOnEmailPreviewScreen = computed(() => {
    return emailValidationRegex.test(email.value) && !errorMessage.value && hasClickedToPreviewEmail.value;
  });

  const clickableLink = computed(() => {
    return linkToChart.value ? linkToChart.value : longLinkToChart.value;
  });

  async function handleModalTypeChanged() {
    if (modalType.value !== EMAIL_MODAL) {
      resetModalState();
      return;
    }

    await generateLinkToChart();
  }

  async function generateLinkToChart() {
    if (
      isInitializingModal.value ||
      (!failedToCreateLinkToChart.value && longLinkToChart.value)
    ) {
      return;
    }

    const { i: styleCacheId, symbol, annotationsRecid, chartType, perfChartSymbolsString, useMainChartSymbolAsBaseline } = layoutStore;

    try {
      resetModalState();

      isInitializingModal.value = true;
      longLinkToChart.value = await getLinkableVersionLong(styleCacheId, symbol, annotationsRecid, chartType, perfChartSymbolsString, useMainChartSymbolAsBaseline);
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Failed to create permalink for styleCacheId: ${styleCacheId} and symbol: ${symbol} and annotationsRecid: ${annotationsRecid} and chartType: ${chartType} and perfChartSymbolsString: ${perfChartSymbolsString} and useMainChartSymbolAsBaseline: ${useMainChartSymbolAsBaseline} from EmailModal.`, e);

      failedToCreateLinkToChart.value = true;
      isInitializingModal.value = false;
      return;
    }

    try {
      linkToChart.value = await getShortUrl(longLinkToChart.value);
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Failed to create short URL for permalink with long URL: ${longLinkToChart.value} for styleCacheId: ${styleCacheId} and symbol: ${symbol} and annotationsRecid: ${annotationsRecid} and chartType: ${chartType} and perfChartSymbolsString: ${perfChartSymbolsString} and useMainChartSymbolAsBaseline: ${useMainChartSymbolAsBaseline} from EmailModal. Using long URL instead.`, e);

      linkToChart.value = longLinkToChart.value;
    }

    isInitializingModal.value = false;
  }

  function previewEmail() {
    if (!emailValidationRegex.test(email.value)) {
      errorMessage.value = 'You must enter a valid email address to share this chart';
      return;
    }

    if (emailComments.value.trim().length > MAX_EMAIL_COMMENTS_LENGTH) {
      errorMessage.value = `Comments must be less than ${MAX_EMAIL_COMMENTS_LENGTH} characters`;
      return;
    }

    errorMessage.value = '';
    hasClickedToPreviewEmail.value = true;
  }

  async function onEmailSend() {
    if (isSendingEmail.value || isInitializingModal.value || modalType.value !== EMAIL_MODAL) {
      return;
    }

    isSendingEmail.value = true;
    const trimmedEmail = email.value.trim();
    const trimmedComments = emailComments.value.trim();
    const linkToChartCopy = linkToChart.value;
    const longLinkToChartCopy = longLinkToChart.value;

    try {
      if (!emailValidationRegex.test(trimmedEmail)) {
        throw new Error('Invalid email address');
      }

      if (trimmedComments.length > MAX_EMAIL_COMMENTS_LENGTH) {
        throw new Error('Comments exceed maximum length');
      }

      const urlSearchParams = new URLSearchParams({
        cmd: 'sendemail',
        toEmail: trimmedEmail,
        linkWorkbenchShort: linkToChartCopy,
        linkChart: longLinkToChartCopy,
        comments: trimmedComments,
        option: 'html'
      });

      const response = await fetch(`/h-sc/ui?${urlSearchParams.toString()}`, {
        method: 'POST',
        credentials: 'include'
      });

      const responseContentType = response.headers.get('content-type');

      if (responseContentType !== 'text/html') {
        throw new Error(`Unexpected response content-type: ${typeof responseContentType} '${responseContentType}', expected 'text/html'.`);
      }

      window.sc3WorkbenchLogger.info(`Email sent with toEmail: ${trimmedEmail} and linkWorkbenchShort: ${linkToChartCopy} and linkChart: ${longLinkToChartCopy} and emailComments: ${trimmedComments}.`);

      addGreenActionResultMessage('Email sent successfully!');
    } catch (e) {
      window.sc3WorkbenchLogger.error(`Failed to send email with toEmail: ${trimmedEmail} and linkWorkbenchShort: ${linkToChartCopy} and linkChart: ${longLinkToChartCopy} and emailComments: ${trimmedComments}.`, e);

      addRedActionResultMessage('Unable to send email. Please try again.');
    }

    isSendingEmail.value = false;

    closeModal();
  }

  function resetModalState() {
    errorMessage.value = '';
    email.value = '';
    emailComments.value = '';
    hasClickedToPreviewEmail.value = false;
    isSendingEmail.value = false;
    isInitializingModal.value = false;
    failedToCreateLinkToChart.value = false;
    longLinkToChart.value = '';
    linkToChart.value = '';
  }

  function goBackFromEmailPreview() {
    hasClickedToPreviewEmail.value = false;
  }

  watch(modalType, handleModalTypeChanged);
</script>

<style scoped>
  .loading-spinner-container,
  .failed-to-load-container,
  .email-ui-container {
    margin: 0px;
    padding: 0px;
  }

  .info {
    margin-top: 0px;
    font-size: 16px;
    color: #4f5964;
    text-align: center;
  }

  .error-message {
    text-align: center;
    border: 1px solid #f5c6cb;
    color: #721c24;
    background-color: #f8d7da;
    border-radius: 4px;
    margin: 0px 0px 8px 0px;
    line-height: 2;
    font-size: 16px;
    padding: 0px;
  }

  .title-message {
    text-align: center;
    border: 1px solid #b8daff;
    color: #004085;
    background-color: #cce5ff;
    border-radius: 4px;
    margin: 0px 0px 8px 0px;
    line-height: 2;
    font-size: 16px;
    padding: 0px;
  }

  .button-row {
    display: flex;
    justify-content: center;
  }

  /* EMAIL PREVIEW CSS */
  .header-logo {
    width: 275px;
    height: 42px;
    border: none;
    outline: none;
    text-decoration: none;
    font-family: Helvetica, arial, sans-serif;
    font-size: 18px;
    color: #4f5458;
  }

  .email-preview-body {
    display: flex;
    flex-direction: column;
    height: min(60vh, 400px);
    overflow-y: auto;
    padding-top: 10px;
  }

  .social-media-links {
    display: flex;
    justify-content: center;
    gap: 5px;
    text-decoration: none;
    padding-top: 15px;
  }

  .social-media-links a {
    padding: 0px;
    text-decoration: none;
  }

  .social-media-links img {
    height: 30px;
    width: 30px;
  }

  .footer {
    padding: 0px 30px;
    border-top: solid 4px #c0ced8;
    margin-right: auto;
    margin-left: auto;
    background-color: #f2f5f7;
  }

  .email-preview-inner-body {
    display: flex;
    flex-direction: column;
    padding: 0px 30px;
    border-top: solid 8px #133353;
    border-right: solid 1px #dfe7ec;
    border-left: solid 1px #dfe7ec;
  }

  .gray-text-line {
    font-family: Helvetica, arial, sans-serif;
    text-align: left;
    line-height: 22px;
    font-size: 16px;
    color: #4f5964;
  }

  i.special-note {
    padding: 15px 0px;
    font-family: Helvetica, arial, sans-serif;
    text-align: left;
    line-height: 16px;
    font-size: 12px;
    color: #707f8f;
  }

  .scc-link {
    text-decoration: none;
    font-weight: bold;
    color: #3281d0;
  }

  .mail-to-tag {
    text-decoration: none;
    color: #707f8f;
  }

  .legal-statement-container {
    padding: 15px 0px;
    text-align: center;
    line-height: 16px;
    font-size: 12px;
    font-weight: normal;
    color: #707f8f;
  }

  .legal-statement-container .legal-statement {
    text-decoration: underline !important;
    font-family: Helvetica, arial, sans-serif;
    font-size: 12px;
    font-weight: normal;
    color: #707f8f;
  }

  .scc-signature {
    text-decoration: none;
    color: #0f4d8a;
  }

  .potential-disclaimer-notice {
    padding-top: 15px;
    text-align: center;
    line-height: 16px;
    font-family: Helvetica, arial, sans-serif;
    font-size: 12px;
    font-weight: normal;
    color: #707f8f;
  }

  .copyright-notice {
    padding-top: 10px;
    text-align: center;
    font-family: Helvetica, arial, sans-serif;
    line-height: 22px;
    font-size: 14px;
    color: #707f8f;
  }

  .copyright-notice a {
    text-decoration: none;
    color: #707f8f;
  }
</style>
